import React, {useMemo, useState} from "react";
import styled, {createGlobalStyle} from "styled-components";
import {Modal, Input, Tag, Spin} from "antd";
import {SearchOutlined, InfoCircleOutlined, CheckOutlined, FireOutlined} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {courierStatusColors, courierStatuses} from "constants/constants";

const ModalGlobalStyle = createGlobalStyle`
    .force-assign-courier-modal .ant-modal-content {
        background: #f5f6f1;
        border-radius: 16px;
        padding: 24px;
    }

    .force-assign-courier-modal .ant-modal-header {
        background: transparent;
        border-bottom: none;
        padding: 0 0 20px 0;
    }

    .force-assign-courier-modal .ant-modal-title {
        font-size: 20px;
        font-weight: 700;
    }

    .force-assign-courier-modal .ant-modal-body {
        padding: 0;
    }

    .force-assign-courier-modal .ant-modal-footer {
        border-top: none;
        padding: 20px 0 0 0;
    }

    .force-assign-courier-modal .ant-modal-close {
        top: 24px;
        right: 24px;
    }

    .force-assign-courier-modal .ant-modal-close-x {
        width: 24px;
        height: 24px;
        line-height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const InfoBox = styled.div`
    background: #f1f2ed;
    border: 1px solid #e5e5e0;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: rgba(0, 0, 0, 0.65);

    .anticon {
        color: #1890ff;
        margin-top: 3px;
        flex: none;
    }
`;

const ListBox = styled.div`
    background: #fff;
    border: 1px solid #e5e5e0;
    border-radius: 10px;
    overflow: hidden;
`;

const SearchInputWrap = styled.div`
    border-bottom: 1px solid #e5e5e0;
`;

const CourierListScroll = styled.div`
    max-height: 280px;
    overflow-y: auto;
    padding: 4px;
`;

const CourierRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 2px 0;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    background: ${props => (props.$selected ? "#e6e7e5" : "transparent")};
    border-color: ${props => (props.$selected ? "#cccecc" : "transparent")};

    &:hover {
        background: ${props => (props.$selected ? "#e6e7e5" : "#eeefeb")};
        border-color: ${props => (props.$selected ? "#cccecc" : "#eeefeb")};
    }
`;

const statusName = status => courierStatuses.find(s => s.value === status)?.name || status;

export const ForceAssignCourierModal = ({isVisible, delivery, onClose, onAssigned}) => {
    const stores = useStore();
    const [searchText, setSearchText] = useState("");
    const [selectedCourierId, setSelectedCourierId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const subCompanyId = delivery?.pickupInfos?.[0]?._id;
    const courierType = delivery?.courierType;

    const [loading, couriers] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getAvailableCouriersForPreferredCourier.bind(null, {
            subCompanyId,
            courierType,
            allPoolCouriers: true
        }),
        dependencies: [isVisible, subCompanyId, courierType],
        condition: Boolean(isVisible && subCompanyId && courierType)
    });

    const filteredCouriers = useMemo(() => {
        const needle = searchText.trim().toLocaleLowerCase("tr-TR");
        if (!needle) return couriers || [];
        return (couriers || []).filter(
            courier =>
                (courier.name || "").toLocaleLowerCase("tr-TR").includes(needle) ||
                (courier.tckn || "").toString().includes(needle)
        );
    }, [couriers, searchText]);

    const reset = () => {
        setSearchText("");
        setSelectedCourierId(null);
    };

    const handleCancel = () => {
        reset();
        onClose();
    };

    const handleOk = async () => {
        if (!selectedCourierId) return;
        setSubmitting(true);
        await stores.orderStore.updateForceCourier({
            deliveryId: delivery?._id,
            forceCourierId: selectedCourierId
        });
        setSubmitting(false);
        reset();
        onAssigned?.();
    };

    return (
        <>
            <ModalGlobalStyle />
            <Modal
                className="force-assign-courier-modal"
                title="Zorunlu Kurye Atama"
                visible={isVisible}
                onCancel={handleCancel}
                onOk={handleOk}
                okText="Zorunlu Atama Yap"
                cancelText="İptal Et"
                okButtonProps={{
                    disabled: !selectedCourierId || submitting,
                    loading: submitting,
                    style: selectedCourierId
                        ? {
                              background: "#AEF001",
                              borderColor: "#AEF001",
                              color: "#000",
                              borderRadius: 8,
                              height: 40,
                              fontWeight: 500
                          }
                        : {
                              background: "#f5f5f5",
                              borderColor: "#d9d9d9",
                              color: "rgba(0, 0, 0, 0.25)",
                              borderRadius: 8,
                              height: 40
                          }
                }}
                cancelButtonProps={{
                    style: {borderRadius: 8, height: 40, borderColor: "#d9d9d9"}
                }}
                maskStyle={{background: "rgba(0, 0, 0, 0.65)"}}
                destroyOnClose
            >
                <InfoBox>
                    <InfoItem>
                        <InfoCircleOutlined />
                        <span>
                            İlgili sipariş için zorunlu kurye ataması yaptığınızda sipariş yalnızca
                            seçilen kuryeye atanabilir, vardiyadaki diğer kuryelere otomatik olarak
                            atanmaz.
                        </span>
                    </InfoItem>
                    <InfoItem>
                        <InfoCircleOutlined />
                        <span>
                            Zorunlu olarak atama yapılan kurye sipariş tamamlanmadan başka bir
                            siparişe otomatik olarak atanamaz.
                        </span>
                    </InfoItem>
                    <InfoItem>
                        <InfoCircleOutlined />
                        <span>
                            Tanımladığınız zorunlu kurye atamasını iptal ederseniz sipariş vardiyada
                            olan kuryelere otomatik olarak atanma akışına geri döner.
                        </span>
                    </InfoItem>
                </InfoBox>

                <ListBox>
                    <SearchInputWrap>
                        <Input
                            allowClear
                            bordered={false}
                            prefix={<SearchOutlined />}
                            placeholder="Kurye ara..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                        />
                    </SearchInputWrap>

                    <CourierListScroll>
                        {loading && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: "32px 0"
                                }}
                            >
                                <Spin />
                            </div>
                        )}
                        {!loading &&
                            filteredCouriers.map(courier => (
                                <CourierRow
                                    key={courier.id}
                                    $selected={selectedCourierId === courier.id}
                                    onClick={() => setSelectedCourierId(courier.id)}
                                >
                                    <span style={{display: "flex", alignItems: "center", gap: 8}}>
                                        {selectedCourierId === courier.id && (
                                            <CheckOutlined style={{color: "#237804"}} />
                                        )}
                                        <span
                                            style={{display: "flex", alignItems: "center", gap: 4}}
                                        >
                                            {courier.isJoker && (
                                                <FireOutlined style={{color: "red"}} />
                                            )}
                                            {courier.name}
                                            {typeof courier.distance === "number" && (
                                                <span
                                                    style={{
                                                        color: "rgba(0,0,0,0.45)",
                                                        fontSize: 12,
                                                        marginLeft: 4
                                                    }}
                                                >
                                                    {courier.distance.toFixed(1)} km
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 4,
                                            minWidth: "200px"
                                        }}
                                    >
                                        {courier.status && (
                                            <Tag
                                                color={
                                                    courierStatusColors[courier.status] || "#d9d9d9"
                                                }
                                                style={{
                                                    fontSize: 10,
                                                    lineHeight: "16px",
                                                    padding: "2px 8px",
                                                    borderRadius: 4
                                                }}
                                            >
                                                {statusName(courier.status)}
                                            </Tag>
                                        )}
                                        {courier.tckn && (
                                            <span
                                                style={{
                                                    color: "rgba(0,0,0,0.45)",
                                                    fontSize: 12
                                                }}
                                            >
                                                {courier.tckn}
                                            </span>
                                        )}
                                    </span>
                                </CourierRow>
                            ))}
                    </CourierListScroll>
                </ListBox>
            </Modal>
        </>
    );
};
